import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { differenceInCalendarDays, isTomorrow } from 'date-fns'
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source'
import { ListChecks, ListTree, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { useUpcomingDayFormat } from '../hooks/useUpcomingDayFormat'
import { resolveTaskIdentifier } from '../utils/taskIdentifier'
import { layoutWeek, upcomingSectionOf } from '../utils/groupUpcoming'
import { useListTaskRowCounts } from './TaskRowCountsProvider'
import type { CSSProperties, ReactNode } from 'react'
import type { TaskRowCounts } from '../hooks/useTaskRowCounts'
import type { UpcomingDay, UpcomingGroups, WeekSpan } from '../utils/groupUpcoming'
import type { TaskReorderEvent } from './TaskList'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { StatusMode } from '@/hooks/useProjectStatusMode'
import { QuickCreateTaskDialog } from '@/components/app/QuickCreateTaskDialog'
import { LocationSwatch } from '@/components/app/sidebars/spaces/SpaceSwatch'
import { TaskStatusCheckbox } from '@/components/app/tasks/TaskStatusCheckbox'
import { useProjectStatusModeMap } from '@/hooks/useProjectStatusModeMap'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import { useTaskMutations } from '@/lib/db/rxdb/hooks'
import { createDragExtractor } from '@/lib/utils/dragDrop'
import { projectAccentColor } from '@/lib/utils/projectIcon'
import { cn } from '@/lib/utils'

const extractTaskDragData = createDragExtractor('task')

/** Overdue is a fixed-width column pinned left; the 7 days share the rest. */
const OVERDUE_WIDTH = 124
/** A span is 32px tall; lanes sit 40px apart below the column's 8px inset. */
const SPAN_HEIGHT = 32
const LANE_STEP = 40
const COLUMN_INSET = 8

/** Inbox has no project colour; its edge and swatch are a neutral grey. */
const INBOX_ACCENT = 'color-mix(in srgb, var(--muted-foreground) 70%, transparent)'

type UpcomingWeekBoardProps = {
  groups: UpcomingGroups
  className?: string
  style?: CSSProperties
  viewMode?: 'simple' | 'split'
  onDropInto: (sectionId: string, event: TaskReorderEvent) => void
  onSelect?: (task: TaskDocument) => void
  selectedTaskId?: string | null
  formatTaskDue: (task: TaskDocument) => string
  projectById: Map<string, ProjectDocument>
}

/**
 * Next 7 days, Week layout (design 34b): Overdue pinned left, then a column
 * per day. A range under way this week is a bar across its days, and dropping
 * a card on another day moves its due date (a range moves as a whole).
 */
export function UpcomingWeekBoard({
  groups,
  className,
  style,
  viewMode,
  onDropInto,
  onSelect,
  selectedTaskId,
  formatTaskDue,
  projectById,
}: UpcomingWeekBoardProps) {
  const { t } = useTranslation('tasks')
  const week = useMemo(() => layoutWeek(groups), [groups])
  const counts = useListTaskRowCounts()
  const { updateTask } = useTaskMutations()
  const { statusModeMap } = useProjectStatusModeMap()
  const { isPersonal } = useWorkspaceMode()
  const [overKey, setOverKey] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [createOn, setCreateOn] = useState<Date | null>(null)

  const showOverdue = groups.overdue.length > 0
  const gridTemplateColumns = `${showOverdue ? `${OVERDUE_WIDTH}px ` : ''}repeat(${groups.days.length}, minmax(0, 1fr))`

  // Spans float over the columns; while a card is dragged they let the
  // pointer through so the column underneath still takes the drop.
  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => Boolean(extractTaskDragData(source.data)),
        onDragStart: () => setIsDragging(true),
        onDrop: () => {
          setIsDragging(false)
          setOverKey(null)
        },
      }),
    [],
  )

  const sectionOf = useCallback(
    (taskId: string) => {
      const task =
        groups.overdue.find((item) => item.id === taskId) ??
        groups.later.find((item) => item.id === taskId) ??
        groups.days.flatMap((day) => day.tasks).find((item) => item.id === taskId)
      return task ? upcomingSectionOf(task, groups) : undefined
    },
    [groups],
  )

  const cardProps = (task: TaskDocument) => {
    const project = task.projectId ? projectById.get(task.projectId) : undefined
    return {
      task,
      project,
      identifier: resolveTaskIdentifier(task, project?.projectKey ?? undefined),
      counts: counts.countsByTask.get(task.id),
      statusMode: (isPersonal ? 'simple' : statusModeMap.get(task.projectId ?? '') ?? 'simple'),
      selected: viewMode === 'split' && selectedTaskId === task.id,
      onSelect,
      onStatusChange: (statusId: string) => {
        if ((task.statusId || null) === (statusId || null)) return
        updateTask(task.id, { statusId }).catch(() => toast.error(t('upcoming.statusFailed')))
      },
    }
  }

  return (
    <div className={cn('flex min-w-0 flex-col pb-4 pt-1', viewMode === 'split' ? 'px-3 md:px-5' : 'px-4 md:px-8', className)} style={style}>
      <div className="min-h-0 flex-1 overflow-auto rounded-xl border bg-card">
        <div className="flex min-h-full min-w-[860px] flex-col">
          {/* One-line day headers (34b): weekday, date, "Tomorrow", count. */}
          <div className="sticky top-0 z-20 grid border-b bg-card" style={{ gridTemplateColumns }}>
            {showOverdue && (
              <div className="flex h-10 items-center gap-1.5 bg-sidebar px-2.5">
                <span className="text-[12.5px] font-semibold text-brand-tint-foreground">{t('upcoming.overdue')}</span>
                <ColumnCount count={groups.overdue.length} />
              </div>
            )}
            {groups.days.map((day, i) => (
              <DayColumnHeader
                key={day.key}
                day={day}
                count={day.tasks.length}
                isOver={overKey === day.key}
                divider={showOverdue || i > 0}
              />
            ))}
          </div>
          <div className="relative grid flex-1" style={{ gridTemplateColumns }}>
            {showOverdue && (
              <div className="flex min-w-0 flex-col gap-1.5 bg-sidebar px-1.5 py-2">
                {groups.overdue.map((task) => (
                  <WeekCard key={task.id} {...cardProps(task)} meta={formatTaskDue(task)} metaTone="overdue" />
                ))}
              </div>
            )}
            {groups.days.map((day, i) => (
              <DayColumn
                key={day.key}
                day={day}
                topInset={COLUMN_INSET + week.lanesByDay[i]! * LANE_STEP}
                divider={showOverdue || i > 0}
                isOver={overKey === day.key}
                canTake={(taskId) => sectionOf(taskId) !== day.key}
                onOverChange={(over) => setOverKey((prev) => (over ? day.key : prev === day.key ? null : prev))}
                onDrop={(taskId) => onDropInto(day.key, { type: 'container', sourceId: taskId, dropPosition: 'end' })}
                onAdd={() => setCreateOn(day.date)}
                dragActive={isDragging}
                isEmpty={week.cardsByDay[i]!.length === 0 && week.lanesByDay[i] === 0}
              >
                {week.cardsByDay[i]!.map((task) => (
                  <WeekCard key={task.id} {...cardProps(task)} meta={formatTaskDue(task)} />
                ))}
              </DayColumn>
            ))}
            {week.spans.map((span) => (
              <WeekSpanBar
                key={span.task.id}
                span={span}
                columns={groups.days.length}
                offset={showOverdue ? OVERDUE_WIDTH : 0}
                passThrough={isDragging}
                cutStart={span.clippedStart}
                cutEnd={span.clippedEnd}
                {...cardProps(span.task)}
              />
            ))}
          </div>
        </div>
      </div>
      <QuickCreateTaskDialog
        open={createOn !== null}
        onOpenChange={(open) => !open && setCreateOn(null)}
        defaultDueDate={createOn}
      />
    </div>
  )
}

function ColumnCount({ count }: { count: number }) {
  return (
    <span className={cn('ml-auto font-mono text-[11px] tabular-nums', count === 0 ? 'text-muted-foreground/60' : 'text-foreground')}>
      {count}
    </span>
  )
}

const isWeekend = (date: Date) => date.getDay() === 0 || date.getDay() === 6

/** Weekends and the drop target read at a glance; weekdays stay white. */
const columnTone = (day: UpcomingDay, isOver: boolean) =>
  isOver ? 'bg-brand-tint/40' : isWeekend(day.date) ? 'bg-sidebar' : 'bg-card'

function DayColumnHeader({
  day,
  count,
  isOver,
  divider,
}: {
  day: UpcomingDay
  count: number
  isOver: boolean
  divider: boolean
}) {
  const { t } = useTranslation('tasks')
  const f = useUpcomingDayFormat()
  return (
    <div
      className={cn('flex h-10 min-w-0 items-center gap-1.5 px-2.5 transition-colors', divider && 'border-l border-border/60', columnTone(day, isOver))}
      title={f.weekdayLongMonthDay(day.date)}
    >
      <span className="text-[11.5px] text-muted-foreground">{f.weekdayShort(day.date)}</span>
      <span className="text-[15px] font-semibold tabular-nums">{f.dayNum(day.date)}</span>
      {isTomorrow(day.date) && (
        <span className="min-w-0 truncate text-[11px] text-muted-foreground">{t('upcoming.tomorrow')}</span>
      )}
      <ColumnCount count={count} />
    </div>
  )
}

function DayColumn({
  day,
  topInset,
  divider,
  isOver,
  canTake,
  onOverChange,
  onDrop,
  onAdd,
  dragActive,
  isEmpty,
  children,
}: {
  day: UpcomingDay
  topInset: number
  divider: boolean
  isOver: boolean
  canTake: (taskId: string) => boolean
  onOverChange: (over: boolean) => void
  onDrop: (taskId: string) => void
  onAdd: () => void
  dragActive: boolean
  isEmpty: boolean
  children: ReactNode
}) {
  const { t } = useTranslation('tasks')
  const f = useUpcomingDayFormat()
  const ref = useRef<HTMLDivElement>(null)
  // Read through a ref so the drop target isn't re-registered every render.
  const handlers = useRef({ canTake, onOverChange, onDrop })
  handlers.current = { canTake, onOverChange, onDrop }

  useEffect(() => {
    const element = ref.current
    if (!element) return
    return dropTargetForElements({
      element,
      // Its own day is a no-op; only another day's card shows the slot.
      canDrop: ({ source }) => {
        const data = extractTaskDragData(source.data)
        return !!data && handlers.current.canTake(data.id)
      },
      getData: () => ({ type: 'upcoming-day', key: day.key }),
      onDragEnter: () => handlers.current.onOverChange(true),
      onDragLeave: () => handlers.current.onOverChange(false),
      onDrop: ({ source }) => {
        handlers.current.onOverChange(false)
        const data = extractTaskDragData(source.data)
        if (data) handlers.current.onDrop(data.id)
      },
    })
  }, [day.key])

  return (
    <div
      ref={ref}
      aria-label={t('upcoming.dropOn', { day: f.weekdayLong(day.date) })}
      className={cn(
        'group/day flex min-w-0 flex-col gap-1.5 px-1.5 pb-2 transition-colors',
        divider && 'border-l border-border/60',
        columnTone(day, isOver),
      )}
      style={{ paddingTop: topInset }}
    >
      {children}
      {isOver ? (
        <div className="flex h-14 flex-col items-center justify-center gap-0.5 rounded-[9px] border-[1.5px] border-dashed border-brand bg-brand-tint/60 text-[11.5px] text-brand-tint-foreground">
          <span className="font-semibold">{t('upcoming.dropToMove')}</span>
          <span>{t('upcoming.dueOn', { date: f.monthDay(day.date) })}</span>
        </div>
      ) : dragActive ? null : (
        <button
          type="button"
          onClick={onAdd}
          aria-label={t('upcoming.addOn', { day: f.weekdayLongMonthDay(day.date) })}
          className={cn(
            'flex h-8 shrink-0 items-center justify-center gap-1 rounded-lg text-xs text-muted-foreground/60 transition-colors hover:bg-secondary hover:text-foreground focus-visible:opacity-100',
            !isEmpty && 'opacity-0 group-hover/day:opacity-100',
          )}
        >
          <Plus className="size-3" aria-hidden />
          {t('upcoming.add')}
        </button>
      )}
    </div>
  )
}

type CardProps = {
  task: TaskDocument
  project?: ProjectDocument
  identifier: string
  counts?: TaskRowCounts
  statusMode: StatusMode
  selected: boolean
  onSelect?: (task: TaskDocument) => void
  onStatusChange: (statusId: string) => void
}

const accentOf = (project?: ProjectDocument) => (project ? projectAccentColor(project) : INBOX_ACCENT)

/** Drag source shared by cards and spans: a tilted clone rides the cursor. */
function useWeekDraggable(taskId: string) {
  const ref = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  // Size at pickup: the ghost left behind keeps the card's footprint.
  const [originHeight, setOriginHeight] = useState(0)
  const [preview, setPreview] = useState<{ container: HTMLElement; width: number } | null>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return
    return draggable({
      element,
      getInitialData: () => ({ type: 'task', id: taskId }),
      onGenerateDragPreview: ({ location, nativeSetDragImage }) => {
        const width = element.getBoundingClientRect().width
        setOriginHeight(element.offsetHeight)
        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: preserveOffsetOnSource({ element, input: location.current.input }),
          render: ({ container }) => {
            setPreview({ container, width })
            return () => setPreview(null)
          },
        })
      },
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    })
  }, [taskId])

  return { ref, isDragging, originHeight, preview }
}

function openOnKey(event: React.KeyboardEvent, open: () => void) {
  if (event.target !== event.currentTarget) return
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    open()
  }
}

/** A day's card (34b): project edge, status, title, then swatch · meta · ID. */
function WeekCard({
  meta,
  metaTone,
  ...props
}: CardProps & { meta: string; metaTone?: 'overdue' }) {
  const { task, selected, onSelect } = props
  const { ref, isDragging, originHeight, preview } = useWeekDraggable(task.id)

  return (
    <>
      {isDragging ? (
        // Where it came from stays marked while it's in the air.
        <div aria-hidden className="shrink-0 rounded-[9px] border border-dashed border-border bg-sidebar" style={{ height: originHeight }} />
      ) : null}
      <div
        ref={ref}
        role="button"
        tabIndex={0}
        data-task-id={task.id}
        onClick={() => onSelect?.(task)}
        onKeyDown={(event) => openOnKey(event, () => onSelect?.(task))}
        className={cn(
          'shrink-0 cursor-pointer rounded-[9px] border bg-card transition-colors hover:border-foreground/20',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          selected && 'border-brand/40 bg-brand-tint/50',
          isDragging && 'hidden',
        )}
        style={{ boxShadow: `inset 3px 0 0 ${accentOf(props.project)}` }}
      >
        <CardBody {...props} meta={meta} metaTone={metaTone} />
      </div>
      {preview &&
        createPortal(
          <div
            className="-rotate-2 rounded-[9px] border bg-card"
            style={{ width: preview.width, boxShadow: `inset 3px 0 0 ${accentOf(props.project)}, 0 14px 28px -10px rgba(31,29,26,.35)` }}
          >
            <CardBody {...props} meta={meta} metaTone={metaTone} inert />
          </div>,
          preview.container,
        )}
    </>
  )
}

function CardBody({
  task,
  project,
  identifier,
  counts,
  statusMode,
  onStatusChange,
  meta,
  metaTone,
  inert,
}: CardProps & { meta: string; metaTone?: 'overdue'; inert?: boolean }) {
  const { t } = useTranslation('tasks')
  return (
    <div className="flex flex-col gap-[7px] py-2 pl-[11px] pr-[9px]">
      <div className="flex items-start gap-[7px]">
        <StatusToggle task={task} statusMode={statusMode} onStatusChange={onStatusChange} inert={inert} />
        <span className="min-w-0 text-[12.5px] font-medium leading-[1.35] [overflow-wrap:anywhere] [text-wrap:pretty]">
          {task.name || t('untitled')}
        </span>
      </div>
      <div className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap text-[11px] text-muted-foreground">
        <span title={project ? project.name : t('upcoming.inbox')} className="flex shrink-0">
          {project ? (
            <LocationSwatch location={project} size={12} />
          ) : (
            <span aria-hidden className="inline-block size-3 rounded-[3px]" style={{ backgroundColor: INBOX_ACCENT }} />
          )}
        </span>
        {meta && (
          // Overdue: the date wins over the ID when space is short.
          <span className={cn('tabular-nums', metaTone === 'overdue' ? 'shrink-0 text-brand-tint-foreground' : 'min-w-0 truncate')}>{meta}</span>
        )}
        {counts && <CardCounts counts={counts} />}
        <span className="min-w-0 flex-1" />
        {identifier && (
          <span className={cn('font-mono text-[10.5px] text-muted-foreground/80', metaTone === 'overdue' ? 'min-w-0 truncate' : 'shrink-0')}>
            {identifier}
          </span>
        )}
      </div>
    </div>
  )
}

function StatusToggle({
  task,
  statusMode,
  onStatusChange,
  inert,
}: Pick<CardProps, 'task' | 'statusMode' | 'onStatusChange'> & { inert?: boolean }) {
  return (
    <span
      className="mt-px flex size-4 shrink-0 items-center justify-center"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {inert ? (
        <span className="size-[13px] rounded-full border-[1.75px] border-muted-foreground/50" />
      ) : (
        <TaskStatusCheckbox currentStatusId={task.statusId} onStatusChange={onStatusChange} statusMode={statusMode} className="size-4" />
      )}
    </span>
  )
}

/** Checklist and sub-task progress, as on list rows (28b). */
function CardCounts({ counts }: { counts: TaskRowCounts }) {
  const { t } = useTranslation('tasks')
  const metric = 'inline-flex shrink-0 items-center gap-0.5 font-mono text-[10.5px] tabular-nums'
  return (
    <>
      {counts.checklistTotal > 0 && (
        <span className={metric} title={t('rowCounts.checklist', { done: counts.checklistDone, total: counts.checklistTotal })}>
          <ListChecks className="size-3" aria-hidden />
          {counts.checklistDone}/{counts.checklistTotal}
        </span>
      )}
      {counts.subTaskTotal > 0 && (
        <span className={metric} title={t('rowCounts.subTasks', { done: counts.subTaskDone, total: counts.subTaskTotal })}>
          <ListTree className="size-3" aria-hidden />
          {counts.subTaskDone}/{counts.subTaskTotal}
        </span>
      )}
    </>
  )
}

/** A range across the days it covers (34b): "Sep 30 → Oct 2 · 3d". */
function WeekSpanBar({
  span,
  columns,
  offset,
  passThrough,
  cutStart,
  cutEnd,
  ...props
}: CardProps & {
  span: WeekSpan
  columns: number
  offset: number
  passThrough: boolean
  cutStart: boolean
  cutEnd: boolean
}) {
  const { t } = useTranslation('tasks')
  const f = useUpcomingDayFormat()
  const { task, selected, onSelect } = props
  const { ref, isDragging, preview } = useWeekDraggable(task.id)
  const dayWidth = `(100% - ${offset}px) / ${columns}`
  const days = differenceInCalendarDays(task.dueDate!, task.startDate!) + 1
  const label = `${t('upcoming.rangeSpan', { start: f.monthDay(task.startDate!), end: f.monthDay(task.dueDate!) })} · ${t('upcoming.rangeDays', { count: days })}`
  const accent = accentOf(props.project)

  const body = (inert?: boolean) => (
    <div className="flex h-full min-w-0 items-center gap-[7px] pl-[11px] pr-2.5">
      <StatusToggle task={task} statusMode={props.statusMode} onStatusChange={props.onStatusChange} inert={inert} />
      <span className="min-w-0 truncate text-[12.5px] font-medium">{task.name || t('untitled')}</span>
      {props.counts && (
        <span className="flex shrink-0 items-center gap-1.5 text-muted-foreground">
          <CardCounts counts={props.counts} />
        </span>
      )}
      <span className="min-w-2 flex-1" />
      <span className="hidden shrink-0 truncate text-[11px] text-muted-foreground @2xs:inline">{label}</span>
      {props.identifier && <span className="shrink-0 font-mono text-[10.5px] text-muted-foreground/80">{props.identifier}</span>}
    </div>
  )

  return (
    <>
      <div
        ref={ref}
        role="button"
        tabIndex={0}
        title={`${task.name} · ${label}`}
        data-task-id={task.id}
        onClick={() => onSelect?.(task)}
        onKeyDown={(event) => openOnKey(event, () => onSelect?.(task))}
        className={cn(
          '@container absolute z-10 cursor-pointer border border-brand/30 bg-brand-tint transition-opacity',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          cutStart ? 'rounded-l-sm' : 'rounded-l-[9px]',
          cutEnd ? 'rounded-r-sm' : 'rounded-r-[9px]',
          selected && 'ring-2 ring-brand/50',
          isDragging && 'opacity-40',
          passThrough && 'pointer-events-none',
        )}
        style={{
          top: COLUMN_INSET + span.lane * LANE_STEP,
          height: SPAN_HEIGHT,
          left: `calc(${offset}px + ${span.from} * ${dayWidth} + 6px)`,
          width: `calc(${span.to - span.from + 1} * ${dayWidth} - 12px)`,
          boxShadow: `inset 3px 0 0 ${accent}`,
        }}
      >
        {body()}
      </div>
      {preview &&
        createPortal(
          <div
            className="-rotate-1 rounded-[9px] border border-brand/30 bg-brand-tint"
            style={{ width: Math.min(preview.width, 360), height: SPAN_HEIGHT, boxShadow: `inset 3px 0 0 ${accent}, 0 14px 28px -10px rgba(31,29,26,.35)` }}
          >
            {body(true)}
          </div>,
          preview.container,
        )}
    </>
  )
}
